<template>
    <div
        class="sf__results__result"
        :class="{
            'sf__results__result--selectable': selectable,
            'sf__results__result--selected': selected,
            'sf__results__result--has-exception': hasException,
            'sf__results__result--full-row-label': fullRowLabel
        }"
        @click="handleCardClick"
    >
        <div class="sf__results__result__loading" v-if="!loaded">
            <!-- Loading -->
        </div>
        <component
            :is="productUrl ? 'a' : 'div'"
            class="sf__results__result__image"
            :href="productUrl"
            @click.stop="handleProductLinkClick"
        >
            <img
                v-bind:class="{
                    'loaded': image_loaded
                }"
                v-if="image"
                :src="image"
                :alt="displayTitle"
                @load="image_loaded = true"
            />
            <p v-else>Image Not Available</p>
        </component>
        <div class="sf__results__result__content">
            <h5 class="sf__results__result__position">{{ formatPosition(part.row) }} - {{ part.side }}</h5>
            <h4 class="sf__results__result__title">
                <a
                    v-if="productUrl"
                    :href="productUrl"
                    @click.stop="handleProductLinkClick"
                >{{ displayTitle }}</a>
                <span v-else>{{ displayTitle }}</span>
            </h4>
            <p class="sf__results__result__sku">{{ part.sku }}</p>
            <p class="sf__results__result__price">
                <span
                    class="sale"
                    v-if="part.price_sale < part.price && eligible"
                >{{ money(part.price_sale) }}</span>
                <span class="regular">{{ money(part.price) }}</span>
            </p>
            <p
                class="sf__results__result__status"
                :class="[statusClass, { 'sf__results__result__status--compact-mobile': compactStatus }]"
                :tabindex="compactStatus ? 0 : null"
                :aria-label="compactStatus ? statusTooltip : null"
                @click="handleStatusClick"
                v-if="status"
            >
                <span class="sf__results__result__status-label">{{ status.label }}</span><span class="sf__results__result__status-separator">:&nbsp;</span>
                <PartStatusDetail :status="status" />
                <span
                    class="sf__results__result__status-tooltip"
                    role="tooltip"
                    v-if="compactStatus"
                >{{ statusTooltip }}</span>
            </p>
        </div>
        <button
            class="sf__results__result__add-to-cart"
            @click.stop="handlePrimaryClick"
            v-bind:class="{
                'disabled': !available,
                'hidden': !loaded && !selectable,
                'sf__results__result__add-to-cart--selected': selectable && selected
            }"
            :disabled="!available || adding"
        >
            <span v-if="adding" class="sf__results__result__add-to-cart__state">
                <span class="sf__results__result__add-to-cart__loader"></span>
            </span>
            <span v-else class="sf__results__result__add-to-cart__state">
                {{ primaryLabel }}
                <svg
                    v-if="selectable && selected"
                    class="sf__results__result__selected-check"
                    width="12"
                    height="9"
                    viewBox="0 0 12 9"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <path d="M3.72667 7.05333L0.946666 4.27333L0 5.21333L3.72667 8.94L11.7267 0.94L10.7867 0L3.72667 7.05333Z" fill="white"/>
                </svg>
            </span>
        </button>
        <button
            type="button"
            class="sf__results__result__selected-radio"
            v-if="selectable"
            :aria-label="selected ? 'Selected' : 'Select option'"
            :disabled="!available || adding"
            @click.stop="handlePrimaryClick"
        ></button>
        <PartPurchaseModal
            v-if="reviewingException"
            :part="part"
            :status="status"
            @close="reviewingException = false"
            @confirm="confirmExceptionAdd"
        />
    </div>
</template>

<style lang="scss">

</style>

<script>
import store from '../store';
import Storage from '../helpers/storage';
import PartPurchaseModal from './PartPurchaseModal.vue';
import PartStatusDetail from './PartStatusDetail.vue';
import {formatPartPosition} from '../helpers/helpers';

export default {
    props: ['part', 'specific', 'selectable', 'selected', 'compactStatus'],

    emits: ['select', 'added'],

    components: {
        PartPurchaseModal,
        PartStatusDetail
    },

    data() {
        return {
            image_loaded: false,
            adding: false,
            atcLabel: 'Add to Cart',
            atcData: null,
            reviewingException: false
        }
    },
    mounted() {
        // Load once the product handle is ready
        this.loadAtcLabel(store.products[this.part.build_id]?.handle);
    },
    watch: {
        'loaded'(val) {
            if (val) {
                const product = store.products[this.part.build_id];
                if (product?.handle) {
                    this.loadAtcLabel(product.handle);
                }
            }
        }
    },

    computed: {
        fullRowLabel() {
            return /^(second|third)\b/i.test(this.part.row || '');
        },
        loaded() {
            return store.products[this.part.build_id] != null;
        },

        available() {
            if (['discontinued', 'out-of-stock', 'back-ordered', 'coming-soon'].includes(this.status?.key)) {
                return false;
            }

            let available = store.products[this.part.build_id]?.available || false;

            // If API returned JSON, override
            if (this.atcData) {
                return !this.atcData?.disabled;
            }
            return available;
        },

        image() {
            const image = store.products[this.part.build_id]?.image
                || this.part.image;

            if (this.specific) {
                return this.part.options.option_1_type_image || image; 
            }

            return image; 
        },

        eligible() {
            return store.products[this.part.build_id]?.eligible;
        },

        product() {
            return store.products[this.part.build_id] || {};
        },

        productUrl() {
            if (!this.product.handle) return null;

            return `/products/${encodeURIComponent(this.product.handle)}`;
        },

        status() {
            return this.product.status || null;
        },

        statusClass() {
            return this.status ? `sf__results__result__status--${this.status.key}` : '';
        },

        statusTooltip() {
            if (!this.status) return '';

            return `This product is ${this.status.label.toLowerCase()}. ${this.status.detail}`;
        },

        hasException() {
            return this.part.exception != null && this.part.exception !== '';
        },

        displayTitle() {
            if (this.specific && this.part.options.option_1_description) {
                return this.part.options.option_1_description;
            }

            return this.part.part;
        },

        primaryLabel() {
            if (!this.available) {
                return this.atcData?.button_label || this.status?.label || 'Out of Stock';
            }

            if (this.selectable) {
                return this.selected ? 'Selected' : 'Select +';
            }

            return 'Add to Cart';
        }
    },

    methods: {
        handleStatusClick(event) {
            if (this.compactStatus) {
                event.stopPropagation();
            }
        },
        handleCardClick() {
            if (this.selectable) {
                this.handlePrimaryClick();
                return;
            }

            this.viewProduct(this.part);
        },

        handleProductLinkClick(event) {
            if (!this.productUrl) {
                event.preventDefault();
                return;
            }

            this.storeRecommendationsInStorage();
            this.part.storeOptionsInStorage();
        },

        handlePrimaryClick() {
            if (!this.available) return;

            if (this.selectable) {
                this.$emit('select', this.part);
                return;
            }

            if (this.hasException) {
                this.reviewingException = true;
                return;
            }

            this.addToCart(this.part);
        },

        viewProduct(part) {
            if (!this.loaded) return false;

            this.storeRecommendationsInStorage();
            part.view();
        },

        async addToCart(part) {
            if (!this.available || !this.loaded) return false;

            this.adding = true;
            try {
                let response = await part.addToCart();
                if (typeof window.sf_options?.afterAddToCart === 'function') {
                    this.storeRecommendationsInStorage();
                    window.sf_options.afterAddToCart(response);
                }
                this.$emit('added', response);
            } catch (error) {
                console.error(error);
            } finally {
                this.adding = false;
            }
        },

        async confirmExceptionAdd() {
            this.reviewingException = false;
            await this.addToCart(this.part);
        },

        storeRecommendationsInStorage() {
            Storage.storeRecommendations(this.part.build_id, store.results_page);
        },

        money(value) {
            if (!value) return null;
            return '$' + parseFloat(value).toFixed(2);
        },

        formatPosition: formatPartPosition,

        async loadAtcLabel(productHandle) {
            // If no product handle, return
            if (!productHandle) return;

            try {
                let url = `${window.location.origin}/products/${productHandle}?view=atc-label`;
                const response = await fetch(url);
                if (!response.ok) throw new Error('Failed to fetch ATC label');
                
                // Get json
                const jsonData = await response.json();

                // Store the full JSON
                this.atcData = jsonData;

                // PLP buttons are purchase-first. Keep unavailable labels from Shopify,
                // but use the Figma Add to Cart label for available standard products.
                this.atcLabel = jsonData.disabled ? (jsonData.button_label || this.atcLabel) : 'Add to Cart';
            } catch (error) {
                this.atcData = null;
            }
        }
    }
}
</script>
